/**
 * home/sections/HeroSection.tsx
 * Sección Hero DS v2: badge · título · stats · dropzone · slider P1→P4 · stepper
 *
 * Integra el slider de pantallas (SS) con 4 mockups .sm definidos en home-v2.css.
 * translateX usa -${active*25}% porque .ss-track crece a 4× el ancho del viewport.
 *
 * @version 2.0.0
 */
import {
  type FC,
  type DragEvent,
  type ChangeEvent,
  type RefObject,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { STATS } from '../data';
// PORT 26-ago: chips y `accept` consumen la fuente única, la misma que el
// wizard, para que la portada no pueda volver a anunciar lo que no se parsea.
import { FORMAT_TAGS, ACCEPT_ATTR } from '../../../lib/acceptedFormats';
import { IconUpload, IconDocument } from '../icons';
import { XCircle, Warning, FileDoc, FileCsv, FileXls, File as FileGeneric, Play } from '@phosphor-icons/react';
import { buscarMunicipio } from '../../../lib/codigosINEUnificado';

// ─── CONFIG SLIDER ────────────────────────────────────────────────────────────

const AUTO_MS = 7000;

const SS_STEPS = [
  { lbl: 'Normaliza' },
  { lbl: 'Valida' },
  { lbl: 'Coteja' },
  { lbl: 'Exporta' },
] as const;

const SS_CAPTIONS: readonly { step: string; strong: string; text: string }[] = [
  { step: 'P1', strong: 'Normaliza',
    text: ' · Detecta y corrige 62 formatos, UTF-8 y truncaciones Y' },
  { step: 'P2', strong: 'Valida',
    text: ' · 6 pestañas: Sin Geolocalizar, Revisar, Correcciones, Sugerencias IA…' },
  { step: 'P3', strong: 'Coteja',
    text: ' · Contrasta infraestructuras con el catálogo DERA (~166 000 features)' },
  { step: 'P4', strong: 'Exporta',
    text: ' · 7 formatos: Excel, GeoJSON, CSV, KML, Shapefile, ODS, HTML · EPSG:25830' },
];

// ─── MUNICIPIO DE DEMOSTRACIÓN ───────────────────────────────────────────────

/**
 * Municipio de ejemplo de las cuatro maquetas del escaparate.
 *
 * Fuente canónica: `src/data/municipios/municipios_andalucia_CONSOLIDADO.json`
 * → Alfacar = INE 18011 · catastro 18012 · CP 18170/18179.
 *
 * Existe como constante única para que la corrección se haga **una vez**: hasta
 * hoy el dato estaba escrito a mano en cinco sitios y dos de ellos publicaban
 * códigos ajenos al municipio — el INE 18008 (catastro de Albuñuelas, y código
 * declarado suprimido en `CODIGOS_INE_SUPRIMIDOS`) y el CP 18210 (de Peligros).
 * Se exporta para que la prueba de canon pueda morder
 * (`OBS-UI-HERO-INE-ALFACAR-ERRONEO`).
 */
export const DEMO_MUNICIPIO = {
  nombre: 'Alfacar',
  provincia: 'Granada',
  ine: '18011',
  codigoPostal: '18170',
} as const;

// ─── SLIDE MOCKS ─────────────────────────────────────────────────────────────

/** P1 — Tabla normalización con scoring 4 colores */
const SlideP1: FC = () => (
  <div className="sm">
    <div style={{background:'rgba(163,230,53,.08)',borderBottom:'1px solid rgba(163,230,53,.2)',
      padding:'6px 14px',fontSize:'10px',color:'var(--fg-3)',display:'flex',alignItems:'center',gap:'6px'}}>
      <svg viewBox="0 0 24 24" style={{width:'11px',height:'11px',stroke:'#A3E635',fill:'none',strokeWidth:2,flexShrink:0}}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/>
      </svg>
      <span><strong style={{color:'var(--fg)'}}>Conversión automática completada.</strong> Verifica elementos con puntuación inferior a 70.</span>
    </div>
    <div className="sm-bar">
      <div className="sm-bar-l"><span>PTEL_Alfacar.odt</span><span className="sm-bar-sep">·</span>
        <span>{DEMO_MUNICIPIO.nombre} ({DEMO_MUNICIPIO.provincia})</span><span className="sm-bar-sep">·</span><span className="sm-bar-ine">{DEMO_MUNICIPIO.ine}</span></div>
      <div className="sm-bar-r"><span style={{color:'#A3E635',fontWeight:600}}>21/27</span> geolocalizados (77%)</div>
    </div>
    <div className="sm-body">
      <div className="sm-prog">
        <div className="sm-prog-hd">
          <span>Resultados de normalización <span style={{color:'var(--fg-3)',fontWeight:400}}>(27 infraestructuras)</span></span>
          <span className="sm-prog-pct">77%</span>
        </div>
        <div className="sm-prog-track">
          <div style={{width:'52%',background:'var(--s-hi)'}}/>
          <div style={{width:'18%',background:'var(--s-md)'}}/>
          <div style={{width:'14%',background:'var(--s-lo)'}}/>
          <div style={{width:'16%',background:'var(--s-cr)'}}/>
        </div>
        <div className="sm-prog-leg">
          <span><span className="sm-prog-dot" style={{background:'var(--s-hi)'}}/>  <b style={{color:'var(--fg)'}}>14</b> ≥90</span>
          <span><span className="sm-prog-dot" style={{background:'var(--s-md)'}}/>  <b style={{color:'var(--fg)'}}>5</b> 70–89</span>
          <span><span className="sm-prog-dot" style={{background:'var(--s-lo)'}}/>  <b style={{color:'var(--fg)'}}>4</b> 50–69</span>
          <span><span className="sm-prog-dot" style={{background:'var(--s-cr)'}}/>  <b style={{color:'var(--fg)'}}>4</b> &lt;50</span>
        </div>
      </div>
      <table className="sm-table">
        <thead><tr><th>Score</th><th>Tipo</th><th>Nombre</th><th>Coord. original</th><th>UTM30 ETRS89</th><th>Fuente</th></tr></thead>
        <tbody>
          <tr><td><span className="sm-sc sm-sc-h">96</span></td><td><span className="sm-tb" style={{background:'rgba(233,30,99,.12)',color:'#e91e63'}}>SAL</span></td><td>Centro Salud Alfacar</td><td className="sm-mono">37°14′28″N 3°34′52″O</td><td className="sm-coord">448923.67 / 4124789.45</td><td className="sm-src-ok">SICESS ±5m</td></tr>
          <tr><td><span className="sm-sc sm-sc-h">94</span></td><td><span className="sm-tb" style={{background:'rgba(33,150,243,.12)',color:'#2196f3'}}>EDU</span></td><td>CEIP García Lorca</td><td className="sm-mono">448.923,67 · 4124.789</td><td className="sm-coord">448923.67 / 4124789.45</td><td className="sm-src-ok">CartoCiudad ±8m</td></tr>
          <tr><td><span className="sm-sc sm-sc-m">78</span></td><td><span className="sm-tb" style={{background:'rgba(244,67,54,.12)',color:'#f44336'}}>EME</span></td><td>Parque Bomberos Nº 3</td><td className="sm-mono">448923 · 124789</td><td className="sm-coord">448923 / 4124789</td><td className="sm-src-md">CDAU ±25m</td></tr>
          <tr><td><span className="sm-sc sm-sc-l">61</span></td><td><span className="sm-tb" style={{background:'rgba(63,81,181,.12)',color:'#7986cb'}}>SEG</span></td><td>Comisaría Local</td><td className="sm-mono" style={{color:'var(--s-cr)'}}>GarcÃ­a Nº 12</td><td className="sm-coord">448501 / 4124312</td><td className="sm-src-lo">Nominatim ±50m</td></tr>
          <tr><td><span className="sm-sc sm-sc-c">22</span></td><td><span className="sm-tb" style={{background:'rgba(156,39,176,.12)',color:'#ba68c8'}}>CUL</span></td><td>Ermita Nª Sª del Carmen</td><td className="sm-mono">—</td><td className="sm-src-no">Sin coordenadas</td><td className="sm-src-no">—</td></tr>
          <tr><td><span className="sm-sc sm-sc-h">89</span></td><td><span className="sm-tb" style={{background:'rgba(233,30,99,.12)',color:'#e91e63'}}>SAL</span></td><td>Consultorio Médico</td><td className="sm-mono">37°14′15″N 3°33′48″O</td><td className="sm-coord">449241.12 / 4124531.78</td><td className="sm-src-ok">SICESS ±5m</td></tr>
        </tbody>
      </table>
    </div>
  </div>
);

/** P2 — Candidatos geocodificación + tabs */
const SlideP2: FC = () => (
  <div className="sm">
    <div className="sm-bar">
      <div className="sm-bar-l"><span>PTEL_Alfacar.odt</span><span className="sm-bar-sep">·</span>
        <span>{DEMO_MUNICIPIO.nombre} ({DEMO_MUNICIPIO.provincia})</span><span className="sm-bar-sep">·</span><span className="sm-bar-ine">{DEMO_MUNICIPIO.ine}</span></div>
      <div className="sm-bar-r"><span style={{color:'#A3E635',fontWeight:600}}>18/27</span> aprobados (66%)</div>
    </div>
    <div className="sm-body">
      <div style={{background:'var(--bg-2)',border:'1px solid var(--border)',borderRadius:'8px',
        padding:'10px 14px',marginBottom:'10px'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',
          marginBottom:'7px',fontSize:'10px'}}>
          <span style={{fontWeight:600}}>Progreso del documento <span style={{color:'var(--fg-3)',fontWeight:400}}>(27 elementos)</span></span>
          <span style={{color:'#A3E635',fontWeight:600}}>66% completado</span>
        </div>
        <div className="sm-prog-track">
          <div style={{width:'66%',background:'var(--s-hi)'}}/>
          <div style={{width:'19%',background:'var(--s-lo)'}}/>
          <div style={{width:'15%',background:'var(--s-cr)'}}/>
        </div>
        <div className="sm-prog-leg">
          <span><span className="sm-prog-dot" style={{background:'var(--s-hi)'}}/>  <b style={{color:'var(--fg)'}}>18</b> aprobados</span>
          <span><span className="sm-prog-dot" style={{background:'var(--s-lo)'}}/>  <b style={{color:'var(--fg)'}}>5</b> pendientes</span>
          <span><span className="sm-prog-dot" style={{background:'var(--s-cr)'}}/>  <b style={{color:'var(--fg)'}}>4</b> sin geolocalizar</span>
        </div>
      </div>
      <div className="sm-tabs">
        <button className="sm-stab sm-stab-a">Sin Geolocalizar <span className="sm-tcnt">4</span></button>
        <button className="sm-stab">Revisar <span className="sm-tcnt">5</span></button>
        <button className="sm-stab">Abreviaturas <span className="sm-tcnt">3</span></button>
        <button className="sm-stab">Correcciones <span className="sm-tcnt">12</span></button>
        <button className="sm-stab">Sugerencias IA <span className="sm-tcnt">2</span></button>
      </div>
      <div className="sm-card">
        <div className="sm-card-hd">
          <div><span className="sm-tb" style={{background:'rgba(156,39,176,.12)',color:'#ba68c8',marginRight:'6px'}}>CUL</span><span className="sm-card-name">Ermita Nª Sª del Carmen</span></div>
          <span className="sm-sc sm-sc-c">22</span>
        </div>
        <div className="sm-card-addr">Calle Mayor s/n · {DEMO_MUNICIPIO.nombre} · {DEMO_MUNICIPIO.codigoPostal}</div>
        <div className="sm-cands">
          <button className="sm-cand">Ermita Ntra. Sra. del Carmen <span className="sm-conf">91%</span></button>
          <button className="sm-cand">Ermita del Carmen (Víznar) <span className="sm-conf">64%</span></button>
          <button className="sm-apply">✓ Aplicar</button>
        </div>
      </div>
      <div className="sm-card">
        <div className="sm-card-hd">
          <div><span className="sm-tb" style={{background:'rgba(244,67,54,.12)',color:'#f44336',marginRight:'6px'}}>EME</span><span className="sm-card-name">Centro de Emergencias 112</span></div>
          <span className="sm-sc sm-sc-c">18</span>
        </div>
        <div className="sm-card-addr">
          <span className="sm-addr-moji-bef">Avda. DiputaciÃ³n 5</span>
          <span className="sm-addr-moji-sep">→</span>
          <span className="sm-addr-moji-aft">Avda. Diputación 5</span>
        </div>
        <div className="sm-cands">
          <button className="sm-cand">Centro 112 Granada <span className="sm-conf">88%</span></button>
          <button className="sm-apply">✓ Aplicar</button>
        </div>
      </div>
    </div>
  </div>
);

/** P3 — Cotejo completitud: ausentes / matches / sobrantes */
const SlideP3: FC = () => (
  <div className="sm">
    <div className="sm-bar">
      <div className="sm-bar-l"><span>PTEL_Alfacar.odt</span><span className="sm-bar-sep">·</span>
        <span>{DEMO_MUNICIPIO.nombre} ({DEMO_MUNICIPIO.provincia})</span><span className="sm-bar-sep">·</span><span className="sm-bar-ine">{DEMO_MUNICIPIO.ine}</span></div>
      <div style={{fontSize:'10px'}}><span style={{color:'var(--s-cr)',fontWeight:700}}>3 ausentes</span> · 1 sobrante · 23 matches</div>
    </div>
    <div className="sm-body">
      <div className="sm-cpills">
        <span className="sm-cpill sm-cpill-cr">Ausentes <b>3</b></span>
        <span className="sm-cpill sm-cpill-hi">Incluidos <b>23</b></span>
        <span className="sm-cpill sm-cpill-lo">Sobrantes <b>1</b></span>
        <span className="sm-cpill" style={{marginLeft:'auto'}}>Todo <b>27</b></span>
      </div>
      <div className="sm-ccard sm-ccard-aus">
        <div style={{flex:1}}>
          <div className="sm-ck sm-ck-a">Ausente</div>
          <div className="sm-cn2">Centro de Salud</div>
          <div className="sm-cm2">Tipología: Sanitario</div>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:'5px',flexShrink:0}}>
          <span className="sm-badge sm-b-cr">Crítica</span>
          <button className="sm-actn sm-actn-cr">+ Añadir</button>
        </div>
      </div>
      <div className="sm-ccard sm-ccard-aus">
        <div style={{flex:1}}>
          <div className="sm-ck sm-ck-a">Ausente</div>
          <div className="sm-cn2">Punto de Encuentro Municipal</div>
          <div className="sm-cm2">Tipología: Emergencias</div>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:'5px',flexShrink:0}}>
          <span className="sm-badge sm-b-lo">Alta</span>
          <button className="sm-actn sm-actn-cr">+ Añadir</button>
        </div>
      </div>
      <div className="sm-ccard sm-ccard-mat">
        <div style={{flex:1}}>
          <div className="sm-ck sm-ck-m">Incluido</div>
          <div className="sm-cn2">CEIP García Lorca</div>
          <div className="sm-cm2">Tipología: Educativo · Confianza: 97% · UTM30: 448923 / 4124789</div>
        </div>
        <button className="sm-actn sm-actn-hi" style={{flexShrink:0}}>Ver en mapa</button>
      </div>
      <div className="sm-ccard sm-ccard-sob">
        <div style={{flex:1}}>
          <div className="sm-ck sm-ck-s">Sobrante</div>
          <div className="sm-cn2">Nave Almacén Municipal</div>
          <div className="sm-cm2">No aparece en catálogo DERA · puede eliminarse del informe</div>
        </div>
        <span className="sm-badge sm-b-hi" style={{flexShrink:0}}>Baja</span>
      </div>
    </div>
  </div>
);

/** P4 — Exportación: panel antes/después + grid formatos + barra descarga */
const SlideP4: FC = () => (
  <div className="sm">
    <div className="sm-bar">
      <div className="sm-bar-l"><span>PTEL_Alfacar.odt</span><span className="sm-bar-sep">·</span>
        <span>{DEMO_MUNICIPIO.nombre}</span><span className="sm-bar-sep">·</span><span className="sm-bar-ine">{DEMO_MUNICIPIO.ine}</span></div>
      <div className="sm-bar-r"><span style={{color:'#A3E635',fontWeight:600}}>24/27</span> geolocalizados (88%)</div>
    </div>
    <div className="sm-body">
      <div style={{fontSize:'12px',fontWeight:700,marginBottom:'2px'}}>Exportar resultados</div>
      <div style={{fontSize:'10px',color:'var(--fg-3)',marginBottom:'8px'}}>Descarga los datos normalizados en el formato que necesites</div>
      <div className="sm-summ">
        <div className="sm-summ-cols">
          <div className="sm-summ-col">
            <h5>Antes</h5>
            <div className="sm-sr"><span className="sm-sr-lbl">Con coordenadas</span><span className="sm-sr-bef">8 (29%)</span></div>
            <div className="sm-sr"><span className="sm-sr-lbl">Errores UTF-8</span><span className="sm-sr-bef">19</span></div>
            <div className="sm-sr"><span className="sm-sr-lbl">Score promedio</span><span className="sm-sr-bef">—</span></div>
          </div>
          <div className="sm-summ-col">
            <h5>Después</h5>
            <div className="sm-sr"><span className="sm-sr-lbl">Geolocalizadas</span><span className="sm-sr-aft">24 (88%)</span></div>
            <div className="sm-sr"><span className="sm-sr-lbl">Errores corregidos</span><span className="sm-sr-aft">19</span></div>
            <div className="sm-sr"><span className="sm-sr-lbl">Score promedio</span><span className="sm-sr-aft">84/100</span></div>
          </div>
        </div>
      </div>
      <div className="sm-fgrid">
        <div className="sm-fc sm-fc-sel"><div className="sm-fi">▦</div><div className="sm-fn">Excel (.xlsx)</div><div className="sm-fd">11 pestañas tipología</div></div>
        <div className="sm-fc sm-fc-sel"><div className="sm-fi">◎</div><div className="sm-fn">GeoJSON</div><div className="sm-fd">Compatible QGIS</div></div>
        <div className="sm-fc"><div className="sm-fi">≡</div><div className="sm-fn">CSV</div><div className="sm-fd">Punto y coma · UTF-8</div></div>
        <div className="sm-fc"><div className="sm-fi">⬡</div><div className="sm-fn">KML</div><div className="sm-fd">Google Earth</div></div>
        <div className="sm-fc sm-fc-sel"><div className="sm-fi">◈</div><div className="sm-fn">Shapefile (.zip)</div><div className="sm-fd">EPSG:25830</div></div>
        <div className="sm-fc"><div className="sm-fi">⊞</div><div className="sm-fn">ODS</div><div className="sm-fd">LibreOffice</div></div>
        <div className="sm-fc"><div className="sm-fi">⌲</div><div className="sm-fn">Informe HTML</div><div className="sm-fd">Resumen municipio</div></div>
      </div>
      <div className="sm-dlbar">
        <div className="sm-dlinfo">
          <strong>27</strong> registros · <strong>3</strong> formatos seleccionados ·{' '}
          <span style={{color:'var(--fg-3)'}}>24 con coordenadas</span>
        </div>
        <button className="sm-dlbtn" style={{background:'#A3E635',color:'#1A1A18'}}>
          ↓ Descargar todo
        </button>
      </div>
    </div>
  </div>
);

const SLIDES = [SlideP1, SlideP2, SlideP3, SlideP4] as const;

// ─── HELPER: icono de archivo según extensión ────────────────────────────────
// Mantiene coherencia visual con los format-tags del estado IDLE
function FileIcon({ ext }: { ext: string }) {
  const e = ext.toLowerCase();
  if (e === 'csv' || e === 'tsv')                   return <FileCsv  size={18} weight="regular" />;
  if (e === 'xlsx' || e === 'xls')                  return <FileXls  size={18} weight="regular" />;
  if (e === 'odt' || e === 'ods' || e === 'doc')    return <FileDoc  size={18} weight="regular" />;
  return <FileGeneric size={18} weight="regular" />;
}

// ─── TYPES ───────────────────────────────────────────────────────────────────

type DropzoneState = 'idle' | 'ready' | 'error' | 'warn';
export interface DropError { type: 'format' | 'multiple'; ext?: string; }

export interface HeroSectionProps {
  /** Está el usuario arrastrando un archivo sobre la dropzone */
  isDragging: boolean;
  /** Ref al input oculto de archivos */
  fileInputRef: RefObject<HTMLInputElement | null>;
  /** Handler dragover en la dropzone */
  onDragOver: (e: DragEvent) => void;
  /** Handler dragleave en la dropzone */
  onDragLeave: () => void;
  /** Handler drop en la dropzone */
  onDrop: (e: DragEvent) => void;
  /** Handler cambio de archivo del input */
  onFileChange: (e: ChangeEvent<HTMLInputElement>) => void;
  /** Usuario autenticado: muestra dropzone; si false, muestra CTA de acceso */
  isAuthenticated: boolean;
  /** Abre el modal de autenticación (CTA estado no logueado) */
  onLoginClick: () => void;
  /** Archivo válido soltado/seleccionado (null = ninguno) */
  dropFile?: File | null;
  /** Error de validación del archivo (null = sin error) */
  dropError?: DropError | null;
  /** Callback para navegar al wizard tras confirmar el archivo */
  onStartNormalization?: () => void;
  /** Callback para resetear el estado del dropzone */
  onReset?: () => void;
  /** Abre el selector de archivo nativo (autenticado) o el modal login */
  onFileSelect?: () => void;
}

// ─── COMPONENT ───────────────────────────────────────────────────────────────

export const HeroSection: FC<HeroSectionProps> = ({
  isDragging, fileInputRef, onDragOver, onDragLeave, onDrop, onFileChange,
  isAuthenticated, onLoginClick,
  dropFile = null, dropError = null, onStartNormalization, onReset, onFileSelect,
}) => {
  const [active, setActive] = useState(0);

  // Estado derivado del dropzone
  const dzState: DropzoneState = (() => {
    if (dropFile && !dropError) return 'ready';
    if (dropError?.type === 'format') return 'error';
    if (dropError?.type === 'multiple') return 'warn';
    return 'idle';
  })();

  // Extrae municipio del nombre de archivo y resuelve INE + provincia + fecha
  const fileInfo = useMemo(() => {
    if (!dropFile) return null;
    const stem = dropFile.name.replace(/\.[^.]+$/, '').replace(/_+/g, ' ').trim();
    const m = stem.match(/PTEL\s+(.+?)(?:\s+v\d+|\s+\d{4}|\s+\d+)?$/i);
    const muniName = m ? m[1].trim() : null;

    // Lookup INE + provincia mediante buscarMunicipio()
    let ine: string | null = null;
    let provincia: string | null = null;
    if (muniName) {
      const res = buscarMunicipio(muniName);
      if (res.encontrado) {
        ine      = res.codigo;
        provincia = res.provincia;
      }
    }

    // Fecha de modificación del archivo (sin hora, en español)
    const fecha = new Date(dropFile.lastModified).toLocaleDateString('es-ES', {
      day: 'numeric', month: 'short', year: 'numeric',
    });

    return { muniName, ine, provincia, fecha };
  }, [dropFile]);

  const detectedMuniName = fileInfo?.muniName ?? null;

  const go = useCallback((n: number) => {
    setActive(((n % 4) + 4) % 4);
  }, []);

  // Auto-avance — se reinicia en cada interacción manual
  useEffect(() => {
    const id = setInterval(() => setActive(a => (a + 1) % 4), AUTO_MS);
    return () => clearInterval(id);
  }, [active]);

  // Ícono check para tabs "done"
  const CheckSvg = () => (
    <svg viewBox="0 0 24 24" style={{width:'9px',height:'9px',stroke:'currentColor',fill:'none',strokeWidth:2.5}}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/>
    </svg>
  );

  return (
    <section className="hero">
      <div className="container">
        <div className="hero__inner">

          {/* ── Badge versión ── */}
          <div className="badge">
            <span className="badge-dot" />
            Andalucía · 785 municipios
          </div>

          <h1>
            Normaliza, geolocaliza<br />y exporta tus PTEL<br />
            <span className="accent">en minutos</span>
          </h1>

          <p className="hero-desc">
            Detecta, corrige y coteja infraestructuras críticas de documentos
            ODT, CSV o Excel con datos de organismos públicos y de OpenStreetMap.
          </p>

          {/* ── Stats ── */}
          <div className="stats">
            {STATS.map((stat) => (
              <div key={stat.label} className="stat">
                <div className="stat-val">{stat.value}</div>
                <div className="stat-lbl">{stat.label}</div>
              </div>
            ))}
          </div>

          {/* ── Formatos admitidos (siempre visibles, estado no autenticado) ── */}
          {!isAuthenticated && (
            <div className="hero-formats">
              {FORMAT_TAGS.map((tag) => (<span key={tag} className="fmt">{tag}</span>))}
            </div>
          )}

          {/* ── Dropzone (solo logueado) ── */}
          {isAuthenticated && (
            /* Estado autenticado: dropzone con estados idle/ready/error/warn */
            <div
              className={[
                'hero-dropzone',
                isDragging && dzState === 'idle' ? 'dragover' : '',
                dzState !== 'idle' ? 'st-' + dzState : '',
              ].filter(Boolean).join(' ')}
              onDragOver={dzState === 'idle' ? onDragOver : undefined}
              onDragLeave={dzState === 'idle' ? onDragLeave : undefined}
              onDrop={dzState === 'idle' ? onDrop : undefined}
              style={{ position: 'relative' }}
              onClick={dzState === 'idle' ? onFileSelect : undefined}
            >
              {/* Input file — siempre en DOM pero invisible; evita label nativo visible */}
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPT_ATTR}
                onChange={onFileChange}
                style={{ display: 'none' }}
              />

              {/* ── Panel IDLE ── */}
              {dzState === 'idle' && (
                <>
                  <div className="dz-icon" style={{ pointerEvents: 'none' }}><IconUpload /></div>
                  <div className="dz-title" style={{ pointerEvents: 'none' }}>Arrastra tu documento PTEL aquí</div>
                  <div className="dz-sub"   style={{ pointerEvents: 'none' }}>o haz clic para seleccionar archivo</div>
                  <span className="dz-btn"  style={{ pointerEvents: 'none' }}><IconDocument />Seleccionar archivo</span>
                  <div className="dropzone__formats" style={{ pointerEvents: 'none' }}>
                    {FORMAT_TAGS.map((tag) => (<span key={tag} className="format-tag">{tag}</span>))}
                  </div>
                  <div className="dz-privacy" style={{ pointerEvents: 'none' }}>
                    El documento permanece en tu navegador · Las coordenadas que confirmes se guardan, con el nombre de la infraestructura y tu cuenta, en servidores situados en la Unión Europea
                  </div>
                </>
              )}

              {/* ── Panel READY ── */}
              {dzState === 'ready' && dropFile && (() => {
                const ext = dropFile.name.split('.').pop() ?? 'odt';
                return (
                  <div className="dz-panel-ready">
                    {/* Layout horizontal: [badge-formato + bloque texto] — — — [botones] */}
                    <div className="dz-rf-main">
                      {/* Badge formato: mismo léxico visual que format-tag del IDLE */}
                      <div className="dz-rf-badge">
                        <FileIcon ext={ext} />
                        <span>{ext.toUpperCase()}</span>
                      </div>
                      {/* Bloque texto: nombre + meta-línea + línea INE */}
                      <div className="dz-rf-info">
                        <div className="dz-rf-name" title={dropFile.name}>{dropFile.name}</div>
                        <div className="dz-rf-meta">
                          {(dropFile.size / 1024).toFixed(1)} KB
                          {detectedMuniName && <> · <span className="dz-rf-meta-muni">{detectedMuniName}</span></>}
                          {' · '}Documento {ext.toUpperCase()}
                        </div>
                        {/* Segunda línea: INE · Provincia · Modificado */}
                        {(fileInfo?.ine || fileInfo?.fecha) && (
                          <div className="dz-rf-meta2">
                            {fileInfo.ine && <span>INE {fileInfo.ine}</span>}
                            {fileInfo.ine && fileInfo.provincia && <span className="dz-rf-sep">·</span>}
                            {fileInfo.provincia && <span>{fileInfo.provincia}</span>}
                            {fileInfo.fecha && <><span className="dz-rf-sep">·</span><span>Modificado: {fileInfo.fecha}</span></>}
                          </div>
                        )}
                      </div>
                    </div>
                    {/* Botones: alineados a la derecha, más abajo */}
                    <div className="dz-rf-actions">
                      <button className="dz-btn-secondary" onClick={onReset} type="button">
                        Cambiar archivo
                      </button>
                      <button className="dz-btn-primary" onClick={onStartNormalization} type="button">
                        <Play size={12} weight="fill" />Iniciar normalización
                      </button>
                    </div>
                  </div>
                );
              })()}

              {/* ── Panel ERROR / WARN ── */}
              {(dzState === 'error' || dzState === 'warn') && (
                <div className="dz-panel-alert">
                  <div className="dz-al-main">
                    <div className={`dz-al-badge dz-al-badge--${dzState}`}>
                      {dzState === 'error'
                        ? <XCircle size={20} weight="fill" />
                        : <Warning  size={20} weight="fill" />
                      }
                    </div>
                    <div className="dz-al-info">
                      <div className="dz-al-title">
                        {dzState === 'error' ? 'Formato no compatible' : 'Un archivo cada vez'}
                      </div>
                      <div className="dz-al-meta">
                        {dzState === 'error'
                          ? <>El archivo{dropError?.ext ? ` .${dropError.ext}` : ''} no está permitido</>
                          : 'Suelta un único archivo para continuar'
                        }
                      </div>
                      {/* Línea 3 — solo en error: formatos compatibles */}
                      {dzState === 'error' && (
                        <div className="dz-al-fmts">
                          <span className="dz-al-fmts-label">Formatos admitidos:</span>
                          {FORMAT_TAGS.map((tag) => (
                            <span key={tag} className="format-tag">{tag}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="dz-al-actions">
                    <button className="dz-btn-primary" onClick={onReset} type="button">
                      Selecciona un archivo
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ══════════════════════════════════
              SCREEN SLIDER P1–P4
          ══════════════════════════════════ */}
          <div className="ss-wrap">
            <div className="ss-eyebrow">Flujo de trabajo · 4 pasos</div>

            <div className="ss-stage">
              {/* Flecha anterior */}
              <button
                className="ss-arrow"
                id="ssPrev"
                aria-label="Anterior"
                disabled={active === 0}
                onClick={() => go(active - 1)}
              >&#8249;</button>

              <div className="ss-viewport">
                {/*
                  translateX: -${active*25}% porque el track crece a 4×
                  el ancho del viewport (4 slides a flex:0 0 100%).
                  25% del track = 1 ancho de viewport.
                */}
                <div
                  className="ss-track"
                  style={{ transform: `translateX(-${active * 25}%)` }}
                >
                  {SLIDES.map((Slide, i) => (
                    <div key={i} className="ss-slide">
                      <div className="ss-frame"><Slide /></div>
                      <div className="ss-caption">
                        <span className="ss-step">{SS_CAPTIONS[i].step}</span>
                        <strong>{SS_CAPTIONS[i].strong}</strong>
                        {SS_CAPTIONS[i].text}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Flecha siguiente */}
              <button
                className="ss-arrow"
                id="ssNext"
                aria-label="Siguiente"
                disabled={active === 3}
                onClick={() => go(active + 1)}
              >&#8250;</button>
            </div>

            {/* Dots indicadores */}
            <div className="ss-dots">
              {SLIDES.map((_, i) => (
                <button
                  key={i}
                  className={`ss-dot${i === active ? ' ss-dot--active' : ''}`}
                  onClick={() => go(i)}
                  aria-label={`Ir al paso ${i + 1}`}
                />
              ))}
            </div>

            {/* Stepper-tab */}
            <div className="ss-stepper">
              {SS_STEPS.map((step, i) => (
                <button
                  key={i}
                  className={[
                    'ss-stab',
                    i === active ? 'ss-stab--active' : '',
                    i < active  ? 'ss-stab--done'   : '',
                  ].filter(Boolean).join(' ')}
                  onClick={() => go(i)}
                >
                  <span className="ss-tab-num">
                    {i < active ? <CheckSvg /> : i + 1}
                  </span>
                  <span className="ss-tab-lbl">{step.lbl}</span>
                </button>
              ))}
            </div>

            {/* Barra de progreso total */}
            <div className="ss-progress">
              <div
                className="ss-progress-fill"
                style={{ width: `${(active + 1) * 25}%` }}
              />
            </div>

          </div>{/* /ss-wrap */}

        </div>{/* /hero__inner */}
      </div>{/* /container */}
    </section>
  );
};

export default HeroSection;
